/* assets/pdp-edd.js
 * PDP Estimated Delivery / Order-By (timezone-aware + fallback window, no-flicker swap)
 * - Cutoff can be UTC or local in an IANA timezone (handles DST)
 * - Units (h/m/s) pulled from data attributes (locale-driven)
 * - Weekend visibility rule (hide on Fri/Sat/Sun after cutoff when showWeekends=false)
 * - SLA hours calculation with weekend rules:
 *     skipWeekends=false → count all days
 *     skipWeekends=true && includeSaturday=true  → skip Sundays only
 *     skipWeekends=true && includeSaturday=false → skip Saturdays & Sundays
 * - TESTING: set window.__EDD_FAKE_NOW = "YYYY-MM-DDTHH:MM:SSZ" to fake current time
 */

(function () {
  const root = document.getElementById('pdp-edd');
  if (!root) return;

  // ---------- Settings from dataset ----------
  const cutoffMode  = (root.dataset.cutoffMode || 'tz').trim(); // 'utc' | 'tz'
  const cutoffTz    = (root.dataset.cutoffTz || 'Europe/London').trim();
  const cutoffLocal = (root.dataset.cutoffLocal || '16:00').trim(); // HH:MM in cutoffTz
  const cutoffUtc   = (root.dataset.cutoffUtc || '16:00').trim();   // HH:MM in UTC (legacy)

  const showWeekends       = root.dataset.showWeekends === 'true';
  const showTimer          = root.dataset.showTimer === 'true';
  const showDate           = root.dataset.showDate === 'true';
  const dateFormat         = (root.dataset.dateFormat || 'dd/mm/yy').trim();
  const slaHours           = parseInt(root.dataset.slaHours || '24', 10);
  const slaExclWeekends    = root.dataset.slaExclWeekends === 'true';
  const slaIncludeSaturday = root.dataset.slaIncludeSaturday === 'true';
  const fallbackWindowHrs  = parseFloat(root.dataset.fallbackWindowHours || '6');

  // Locale-provided unit labels (fallbacks)
  const units = {
    hr:   root.dataset.unitHr   || 'hour',
    hrs:  root.dataset.unitHrs  || 'hours',
    min:  root.dataset.unitMin  || 'minute',
    mins: root.dataset.unitMins || 'minutes',
    sec:  root.dataset.unitSec  || 'second',
    secs: root.dataset.unitSecs || 'seconds'
  };

  // DOM targets
  const $line = root.querySelector('[data-edd-line]');
  const $tpl  = document.getElementById('pdp-edd-dynamic-tpl');

  // Will be (re)queried after swap
  let $timer = null;
  let $date  = null;

  // ---------- Utilities ----------
  function nowUtc() {
    // DEBUG/testing: override "now"
    if (window.__EDD_FAKE_NOW) return new Date(window.__EDD_FAKE_NOW);
    return new Date();
  }

  function parseHHMM(s, fallback = '16:00') {
    const str = (s || fallback).trim();
    const m = /^(\d{1,2}):(\d{2})$/.exec(str) || /^(\d{1,2})$/.exec(str);
    if (!m) return { h: 16, m: 0 };
    if (m.length === 3) return { h: Math.min(23, +m[1]), m: Math.min(59, +m[2]) };
    return { h: Math.min(23, +m[1]), m: 0 };
  }

  function partsInZone(instant, timeZone) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    const p = fmt.formatToParts(instant).reduce((o, x) => (o[x.type] = x.value, o), {});
    return { y: +p.year, M: +p.month, d: +p.day, h: +p.hour, m: +p.minute, s: +p.second };
  }

  // Convert wall time in a timezone to the corresponding UTC instant (robust across DST)
  function zonedWallTimeToUtc(y, M, d, h, m, timeZone) {
    let guess = new Date(Date.UTC(y, M - 1, d, h, m, 0, 0));
    for (let i = 0; i < 3; i++) {
      const p = partsInZone(guess, timeZone);
      const deltaMinutes =
        (h - p.h) * 60 +
        (m - p.m) +
        (y - p.y) * 525600 +
        (M - p.M) * 44640 +
        (d - p.d) * 1440;
      if (deltaMinutes === 0) break;
      guess = new Date(guess.getTime() + deltaMinutes * 60000);
    }
    return guess;
  }

  function cutoffInstantFor(instantUtc) {
    if (cutoffMode === 'utc') {
      const { h, m } = parseHHMM(cutoffUtc);
      return new Date(Date.UTC(
        instantUtc.getUTCFullYear(),
        instantUtc.getUTCMonth(),
        instantUtc.getUTCDate(),
        h, m, 0, 0
      ));
    } else {
      const { h, m } = parseHHMM(cutoffLocal);
      const p = partsInZone(instantUtc, cutoffTz);
      return zonedWallTimeToUtc(p.y, p.M, p.d, h, m, cutoffTz);
    }
  }

  function nextCutoffInstantFrom(now) {
    const today = cutoffInstantFor(now);
    if (now.getTime() < today.getTime()) return today;
    const tomorrow = new Date(now.getTime() + 86400000);
    if (cutoffMode === 'utc') {
      const { h, m } = parseHHMM(cutoffUtc);
      return new Date(Date.UTC(
        tomorrow.getUTCFullYear(),
        tomorrow.getUTCMonth(),
        tomorrow.getUTCDate(),
        h, m, 0, 0
      ));
    } else {
      const { h, m } = parseHHMM(cutoffLocal);
      const p = partsInZone(tomorrow, cutoffTz);
      return zonedWallTimeToUtc(p.y, p.M, p.d, h, m, cutoffTz);
    }
  }

  function isFriSatSunInTz(instant) {
    const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: cutoffTz, weekday: 'short' });
    const wk = fmt.format(instant);
    return wk === 'Fri' || wk === 'Sat' || wk === 'Sun';
  }

  function afterTodayCutoffInTz(now) {
    return now.getTime() >= cutoffInstantFor(now).getTime();
  }

  // Add hours; optionally skip weekend hours with Saturday inclusion rule
  function addSlaHours(startUtc, hours, skipWeekends, includeSaturday) {
    if (!skipWeekends) return new Date(startUtc.getTime() + hours * 3600000);

    let remaining = hours;
    let current = new Date(startUtc);

    while (remaining > 0) {
      const nextHour = new Date(current.getTime() + 3600000);

      const weekday = new Intl.DateTimeFormat('en-GB', {
        timeZone: cutoffTz,
        weekday: 'short'
      }).format(nextHour);

      // If excluding weekends:
      // - includeSaturday=true → skip Sundays only
      // - includeSaturday=false → skip Sat & Sun
      const isSunday = (weekday === 'Sun');
      const isSaturday = (weekday === 'Sat');

      if (isSunday || (isSaturday && !includeSaturday)) {
        const p = partsInZone(nextHour, cutoffTz);
        const addDays = isSunday ? 1 : 2; // Sun->Mon (+1), Sat->Mon (+2)
        const mondayLocalUtc = zonedWallTimeToUtc(p.y, p.M, p.d + addDays, 0, 0, cutoffTz);
        current = mondayLocalUtc;
        continue;
      }

      current = nextHour;
      remaining -= 1;
    }
    return current;
  }

  function formatDate(d, fmt) {
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    const yy = String(yyyy).slice(-2);
    const ddd = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getUTCDay()];
    const mmm = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];

    switch (fmt) {
      case 'mm/dd/yy':   return `${mm}/${dd}/${yy}`;
      case 'yyyy-mm-dd': return `${yyyy}-${mm}-${dd}`;
      case 'ddd d mmm':  return `${ddd} ${d.getUTCDate()} ${mmm}`;
      case 'dd/mm/yy':
      default:           return `${dd}/${mm}/${yy}`;
    }
  }

  // "3h 12m 9s" — no space between number and unit, spaces between groups
  function formatHMS(msRemaining) {
    if (msRemaining <= 0) return `0${units.secs}`;
    const totalSec = Math.floor(msRemaining / 1000);
    const hrs  = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;

    const parts = [];
    if (hrs > 0) parts.push(`${hrs}${hrs === 1 ? units.hr : units.hrs}`);
    if (mins > 0) parts.push(`${mins}${mins === 1 ? units.min : units.mins}`);
    parts.push(`${secs}${secs === 1 ? units.sec : units.secs}`);
    return parts.join(' ');
  }

  // ---------- Update loop ----------
  function update() {
    const now = nowUtc();

    // Weekend visibility rule: hide on Fri/Sat/Sun AFTER the cutoff (in market timezone) when showWeekends=false
    if (!showWeekends && isFriSatSunInTz(now) && afterTodayCutoffInTz(now)) {
      root.hidden = true;
      return;
    } else {
      root.hidden = false;
    }

    // Decide whether to show fallback or dynamic
    const nextCutoff = nextCutoffInstantFrom(now);
    const msToCutoff = nextCutoff.getTime() - now.getTime();
    const withinWindow = msToCutoff <= fallbackWindowHrs * 3600000;

    // Ensure we never show a blank line (e.g., theme AJAX re-render)
    if ($line && !$line.textContent.trim()) {
      const tpl = root.dataset.fallbackTemplate || '';
      const label = root.dataset.cutoffLabel || '';
      $line.textContent = tpl.replace('{{cutoff_time}}', label);
    }

    if (withinWindow && $line && $tpl && ($line.dataset.mode !== 'dynamic')) {
      // Swap once to dynamic
      // Prefer parsed HTML via <template> if possible
      if ('content' in $tpl) {
        $line.innerHTML = '';
        $line.appendChild($tpl.content.cloneNode(true));
      } else {
        $line.innerHTML = $tpl.innerHTML;
      }
      $line.dataset.mode = 'dynamic';
      // Re-query dynamic nodes
      $timer = root.querySelector('[data-timer]');
      $date  = root.querySelector('[data-edd-date]');
    } else if (!withinWindow && $line && $line.dataset.mode === 'dynamic') {
      // Swap back to fallback if we left the window (e.g., section reload)
      const tpl = root.dataset.fallbackTemplate || '';
      const label = root.dataset.cutoffLabel || '';
      $line.textContent = tpl.replace('{{cutoff_time}}', label);
      delete $line.dataset.mode;
      $timer = null;
      $date  = null;
    }

    // Timer (only meaningful after swap when the timer element exists)
    if (showTimer && $timer) {
      $timer.textContent = formatHMS(msToCutoff);
    }

    // EDD: base is now (before cutoff) or next cutoff (after cutoff), then add SLA hours
    if (showDate && $date) {
      const orderBase = afterTodayCutoffInTz(now) ? nextCutoff : now;
      const eddUtc = addSlaHours(orderBase, slaHours, slaExclWeekends, slaIncludeSaturday);
      $date.textContent = formatDate(eddUtc, dateFormat);
    }
  }

  update();
  const interval = setInterval(update, 1000);

  // Clean up when page is backgrounded
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearInterval(interval);
  }, { once: true });
})();
